import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { AdminUpdateProfileRequest, LookupEntry } from '../types/admin-garage-edit';
import type { GarageDetailResponse } from '../types';
import MapPicker from './MapPicker';

/** Spec 081a: server limit for the garage description, each language. */
const DESCRIPTION_MAX = 1000;

interface EditGarageProfileDialogProps {
  garage: GarageDetailResponse;
  onClose: () => void;
  onSaved: () => void;
}

export default function EditGarageProfileDialog({ garage, onClose, onSaved }: EditGarageProfileDialogProps) {
  const { token } = useAuth();
  const [form, setForm] = useState<AdminUpdateProfileRequest>({
    name: garage.name ?? '',
    phone: garage.phone ?? '',
    email: garage.email ?? '',
    licenseNumber: garage.licenseNumber ?? '',
    cityId: (garage as unknown as { cityId?: string }).cityId,
    areaId: (garage as unknown as { areaId?: string }).areaId,
    streetAddress: garage.streetAddress ?? '',
    managerName: garage.managerName ?? '',
    numberOfWorkers: garage.numberOfWorkers ?? undefined,
    latitude: garage.latitude != null ? Number(garage.latitude) : undefined,
    longitude: garage.longitude != null ? Number(garage.longitude) : undefined,
    description: garage.description ?? '',
    descriptionAr: garage.descriptionAr ?? '',
  });
  const [cities, setCities] = useState<LookupEntry[]>([]);
  const [areas, setAreas] = useState<LookupEntry[]>([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = useCallback(<K extends keyof AdminUpdateProfileRequest>(k: K, v: AdminUpdateProfileRequest[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError(null);
  }, []);

  // Fetch cities once. If the garage already has a city name (legacy), we'll
  // try to match it to a LookupEntry by name as a soft preselect.
  useEffect(() => {
    if (!token) return;
    api.fetchLookupEntries('CITIES', undefined, token)
      .then((list) => {
        setCities(list);
        if (!form.cityId && garage.city) {
          const match = list.find((c) => c.name === garage.city);
          if (match) setForm((p) => ({ ...p, cityId: match.id }));
        }
      })
      .catch(() => { /* silent — dropdown will be empty */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Refetch areas whenever cityId changes.
  useEffect(() => {
    if (!token || !form.cityId) { setAreas([]); return; }
    setAreasLoading(true);
    api.fetchLookupEntries('AREAS', form.cityId, token)
      .then((list) => {
        setAreas(list);
        if (!form.areaId && garage.area) {
          const match = list.find((a) => a.name === garage.area);
          if (match) setForm((p) => ({ ...p, areaId: match.id }));
        }
      })
      .catch(() => setAreas([]))
      .finally(() => setAreasLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, form.cityId]);

  const handleMapChange = useCallback((lat: number, lng: number) => {
    setForm((p) => ({ ...p, latitude: lat, longitude: lng }));
    setError(null);
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      // Strip legacy free-text city/area when we have IDs — backend prefers the FK path.
      const payload: AdminUpdateProfileRequest = { ...form };
      if (payload.cityId) delete payload.city;
      if (payload.areaId) delete payload.area;
      // Spec 081a: send the description only when it changed ("" clears it), so an untouched
      // field neither writes "" over null nor adds a no-op line to the audit.
      if ((payload.description ?? '') === (garage.description ?? '')) delete payload.description;
      if ((payload.descriptionAr ?? '') === (garage.descriptionAr ?? '')) delete payload.descriptionAr;
      await api.adminGarageUpdateProfile(garage.id, payload, token);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const sortedCities = useMemo(() => [...cities].sort((a, b) => a.name.localeCompare(b.name)), [cities]);
  const sortedAreas = useMemo(() => [...areas].sort((a, b) => a.name.localeCompare(b.name)), [areas]);

  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <form className="admin-modal admin-modal-lg" onSubmit={onSubmit} style={{ width: 760, maxWidth: '95vw' }}>
        <div className="admin-modal-header"><h3>Edit Garage Profile</h3></div>
        <div className="admin-modal-body" style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr' }}>
          <label>Name<input className="admin-input" required value={form.name} onChange={(e) => set('name', e.target.value)} /></label>
          <label>Phone<input className="admin-input" required value={form.phone} onChange={(e) => set('phone', e.target.value)} /></label>
          <label>Email<input className="admin-input" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} /></label>
          <label>License Number<input className="admin-input" value={form.licenseNumber ?? ''} onChange={(e) => set('licenseNumber', e.target.value)} /></label>

          <label>City
            <select
              className="admin-input"
              value={form.cityId ?? ''}
              onChange={(e) => set('cityId', e.target.value || undefined)}
            >
              <option value="">Select a city…</option>
              {sortedCities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>Area
            <select
              className="admin-input"
              value={form.areaId ?? ''}
              onChange={(e) => set('areaId', e.target.value || undefined)}
              disabled={!form.cityId || areasLoading}
            >
              <option value="">{form.cityId ? (areasLoading ? 'Loading…' : 'Select an area…') : 'Select a city first'}</option>
              {sortedAreas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>

          <label style={{ gridColumn: '1 / -1' }}>Street Address<input className="admin-input" value={form.streetAddress ?? ''} onChange={(e) => set('streetAddress', e.target.value)} /></label>
          <label>Manager Name<input className="admin-input" value={form.managerName ?? ''} onChange={(e) => set('managerName', e.target.value)} /></label>
          <label>Workers<input className="admin-input" type="number" value={form.numberOfWorkers ?? ''} onChange={(e) => set('numberOfWorkers', e.target.value ? Number(e.target.value) : undefined)} /></label>

          {([
            { key: 'description', lang: 'en', label: 'Description (English)' },
            { key: 'descriptionAr', lang: 'ar', label: 'Description (Arabic)' },
          ] as const).map((f) => {
            const value = form[f.key] ?? '';
            const atLimit = value.length >= DESCRIPTION_MAX;
            return (
              <label key={f.key} className="admin-desc-field">
                {f.label}
                <textarea
                  className="admin-input"
                  dir={f.lang === 'ar' ? 'rtl' : 'ltr'}
                  lang={f.lang}
                  rows={4}
                  maxLength={DESCRIPTION_MAX}
                  value={value}
                  aria-describedby={`admin-garage-${f.key}-count`}
                  data-testid={`admin-garage-${f.key}`}
                  onChange={(e) => set(f.key, e.target.value)}
                />
                <span id={`admin-garage-${f.key}-count`} className={`admin-desc-counter${atLimit ? ' admin-desc-counter--limit' : ''}`}>
                  {value.length}/{DESCRIPTION_MAX}
                </span>
              </label>
            );
          })}

          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Location (search, or click on the map to drop a pin)
            </div>
            <MapPicker
              latitude={form.latitude ?? null}
              longitude={form.longitude ?? null}
              onChange={handleMapChange}
              searchPlaceholder="Search a mall, street or area"
            />
            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
              {form.latitude != null && form.longitude != null
                ? `Selected: ${form.latitude.toFixed(6)}, ${form.longitude.toFixed(6)}`
                : 'No location selected yet.'}
            </div>
          </div>

          {error && <div className="admin-error" style={{ gridColumn: '1 / -1' }}>{error}</div>}
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}
